// Piezas compartidas de Inteligencia › Maquinaria, Entradas y salidas, Materiales y Acopio
// (mismo estilo blanco del Dashboard IA y del Calendario de obra).
import React, { useEffect, useState } from 'react';
import { T, Btn, inputStyle } from '../ui';
import { todayKey, dayKey, fmtKey, addDays } from '../iaData';
import { cargarFotosV3, rangoDefecto } from './datosV3';

export function useNarrow(px = 720) {
  const q = `(max-width:${px}px)`;
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const f = () => setM(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', f) : mq.addListener(f);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', f) : mq.removeListener(f));
  }, [q]);
  return m;
}

// Fotos iaV3 del rango. Si la cámara no tiene fotos analizadas en el rango pero sí antes, se corre el rango
// a la última semana analizada (así nunca se ve "vacío" cuando hay datos).
export function useFotosV3(feedId, dias = 7) {
  const [rango, setRango] = useState(() => rangoDefecto(todayKey(), dias));
  const [estado, setEstado] = useState({ cargando: true, fotos: [], fuente: null });
  useEffect(() => {
    let vivo = true;
    setEstado((e) => ({ ...e, cargando: true }));
    cargarFotosV3(feedId, rango.desde, rango.hasta).then((r) => {
      if (!vivo) return;
      if (!r.fotos.length && r.ultimo && dayKey(r.ultimo) < rango.desde) {
        setRango(rangoDefecto(dayKey(r.ultimo), dias));
        return;
      }
      setEstado({ cargando: false, ...r });
    });
    return () => { vivo = false; };
  }, [feedId, rango.desde, rango.hasta, dias]);
  return { rango, setRango, ...estado };
}

// Encabezado de cada página: título, obra y el rango de fechas
export function Encabezado({ icono, titulo, obra, bajada, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: T.text, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span aria-hidden>{icono}</span>{titulo}
        </h2>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 2 }}>{obra ? <b style={{ color: T.text, fontWeight: 600 }}>{obra}</b> : null}{obra && bajada ? ' · ' : ''}{bajada}</div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>{children}</div>
    </div>
  );
}

// Selector de rango (desde / hasta + atajos)
export function Rango({ rango, setRango, maxDias = 35 }) {
  const hoy = todayKey();
  const fijar = (desde, hasta) => {
    if (hasta > hoy) hasta = hoy;
    if (desde > hasta) desde = hasta;
    if (Date.parse(hasta) - Date.parse(desde) > (maxDias - 1) * 864e5) desde = addDays(hasta, -(maxDias - 1));
    setRango({ desde, hasta });
  };
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <input type="date" aria-label="Desde" value={rango.desde} max={hoy} onChange={(e) => e.target.value && fijar(e.target.value, rango.hasta)} style={inputStyle} />
      <span style={{ color: T.muted, fontSize: 13 }}>a</span>
      <input type="date" aria-label="Hasta" value={rango.hasta} max={hoy} onChange={(e) => e.target.value && fijar(rango.desde, e.target.value)} style={inputStyle} />
      <Btn onClick={() => fijar(addDays(hoy, -6), hoy)}>7 días</Btn>
      <Btn onClick={() => fijar(addDays(hoy, -29), hoy)}>30 días</Btn>
      <Btn onClick={() => fijar(addDays(hoy, -34), hoy)} title="Los últimos 35 días">Mes completo</Btn>
    </div>
  );
}

// Aviso honesto (qué es aproximado y por qué)
export function Nota({ children, tono = 'info' }) {
  const c = tono === 'aviso' ? { bg: '#fffbeb', bd: '#fde68a', tx: '#92400e' } : { bg: '#eff6ff', bd: '#bfdbfe', tx: '#1e40af' };
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.bd}`, color: c.tx, borderRadius: 12, padding: '8px 12px', fontSize: 12.5, lineHeight: 1.45 }}>
      {children}
    </div>
  );
}

export function Cargando({ texto = 'Buscando en las fotos de la cámara…' }) {
  return (
    <div style={{ padding: 32, textAlign: 'center', color: T.muted, fontSize: 14 }}>
      <div style={{ width: 28, height: 28, margin: '0 auto 10px', border: '3px solid #dbeafe', borderTopColor: T.blue, borderRadius: '50%', animation: 'lisualGiro 0.9s linear infinite' }} />
      <style>{'@keyframes lisualGiro{to{transform:rotate(360deg)}}'}</style>
      {texto}
    </div>
  );
}

export function Vacio({ children }) {
  return <div style={{ padding: 28, textAlign: 'center', color: T.muted, fontSize: 14, lineHeight: 1.5 }}>{children}</div>;
}

export const Kpi = ({ label, valor, sub, color }) => (
  <div style={{ background: '#fff', border: `1px solid ${T.line}`, borderRadius: 16, padding: '10px 14px', minWidth: 0 }}>
    <div style={{ fontSize: 12, color: T.muted }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color: color || T.text, lineHeight: 1.2 }}>{valor}</div>
    {sub && <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>{sub}</div>}
  </div>
);

export const textoRango = (r) => (r.desde === r.hasta ? fmtKey(r.desde) : `${fmtKey(r.desde)} al ${fmtKey(r.hasta)}`);

// Sin fotos analizadas por el modelo nuevo
export function SinAnalisis() {
  return (
    <Vacio>
      Esta cámara todavía no tiene fotos analizadas con el modelo nuevo de reconocimiento de objetos.<br />
      Cuando se active, vas a ver acá lo que encontró en las fotos.
    </Vacio>
  );
}
